/**
 * Editor Types
 * Shared type definitions for the document editor system
 * 
 */

// =============================================================================
// DOCUMENT TYPES
// =============================================================================

export type DocumentType = 'regular' | 'master' | 'control';
export type ComponentType = 'textEditor' | 'spreadsheet' | 'gantt';

export interface AutosaveConfig {
  enabled: boolean;
  debounceMs: number;
  maxIntervalMs: number;
}

export interface AutosavePolicy {
  defaultEnabled: boolean;
  allowUserOverride: boolean;
  isTemplatePolicy: boolean;
  /** Raw template default — always reflects the template's enableAutoSave value (or true for non-template). Used to seed new version defaults on saveAs. */
  templateDefaultEnabled: boolean;
}

// =============================================================================
// EDITOR CONFIGURATION
// =============================================================================

export interface UserInfo {
  id: string;
  name: string;
  email: string;
}

export interface EditorPermissions {
  save: boolean;
  saveAs: boolean;
  export: boolean;
  print: boolean;
}

export interface CommentsConfig {
  currentUser: UserInfo;
  mentionableUsers: UserInfo[];
}

export interface DocumentMetadata {
  name: string;
  version: string;
  id?: string;
}

export interface EditorConfig {
  /** Target Syncfusion component */
  componentType?: ComponentType;
  /** Document type determines ribbon visibility and editing restrictions */
  documentType: DocumentType;
  
  /** Whether user can edit the document */
  canEdit: boolean;

  /** Whether user can add/save comments while document content remains locked */
  canComment?: boolean;
  
  /** Granular permissions for file menu actions */
  permissions: EditorPermissions;
  
  /** Comment system configuration */
  comments: CommentsConfig;
  
  /** Optional document metadata */
  metadata?: DocumentMetadata;
  autosave?: AutosaveConfig;
  
  /** Whether version creation is disabled by lifecycle template */
  allowVersionCreationDisabled?: boolean;
  autosavePolicy?: AutosavePolicy;
}

// =============================================================================
// EDITOR STATE MACHINE
// =============================================================================

export type EditorState =
  | 'IDLE'                  // Initial state, waiting for iframe communication
  | 'WAITING_FOR_CONFIG'    // Editor ready, waiting for config from parent
  | 'CONFIGURING'           // Applying configuration
  | 'READY'                 // Configured, waiting for document
  | 'LOADING_DOCUMENT'      // Loading document content
  | 'APPLYING_PROTECTION'   // Applying protection (Control Copy)
  | 'ACTIVE'                // Fully operational
  | 'ERROR';                // Error state

export type EditorAction =
  | { type: 'EDITOR_CREATED' }
  | { type: 'CONFIG_RECEIVED'; payload: EditorConfig }
  | { type: 'CONFIG_APPLIED' }
  | { type: 'LOAD_DOCUMENT' }
  | { type: 'DOCUMENT_LOADED' }
  | { type: 'APPLY_PROTECTION' }
  | { type: 'PROTECTION_APPLIED' }
  | { type: 'ACTIVATE_EDITOR' }
  | { type: 'REINITIALIZE'; payload: EditorConfig }
  | { type: 'ERROR'; payload: EditorError }
  | { type: 'RESET' };

export interface EditorStateContext {
  state: EditorState;
  config: EditorConfig | null;
  error: EditorError | null;
  isDocumentLoaded: boolean;
  hasUnsavedChanges: boolean;
}

// =============================================================================
// ERROR HANDLING
// =============================================================================

export type EditorErrorCode =
  | 'CONFIG_INVALID'
  | 'CONFIG_TIMEOUT'
  | 'DOCUMENT_LOAD_FAILED'
  | 'PROTECTION_FAILED'
  | 'COMMUNICATION_ERROR'
  | 'SYNCFUSION_ERROR'
  | 'UNKNOWN_ERROR';

export interface EditorError {
  code: EditorErrorCode;
  message: string;
  details?: unknown;
  timestamp: number;
}

export function createEditorError(
  code: EditorErrorCode,
  message: string,
  details?: unknown
): EditorError {
  return {
    code,
    message,
    details,
    timestamp: Date.now(),
  };
}

// =============================================================================
// SAVE PAYLOADS
// =============================================================================

export interface CommentData {
  commentId: string;
  author: string;
  authorId: string | null;
  text: string;
  mentions: string[];
  date: string;
  isResolved: boolean;
  replies: Array<{
    commentId: string;
    author: string;
    authorId: string | null;
    text: string;
    mentions: string[];
    date: string;
  }>;
}

export interface SavePayload {
  content: {
    sfdt?: string;
    spreadsheetJson?: string;
    ganttJson?: string;
    html?: string;
  };
  comments: {
    allComments: CommentData[];
    /** One entry per new/edited comment or reply; each entry lists the user IDs tagged in that comment. */
    sessionMentions: Array<{ users: string[] }>;
  };
  metadata: {
    documentName: string;
    timestamp: number;
    componentType?: ComponentType;
  };
}

// =============================================================================
// FILE MENU CONFIGURATION
// =============================================================================

export interface FileMenuItem {
  id: string;
  text: string;
  iconCss?: string;
  disabled?: boolean;
}

export type BuiltInFileMenuItem = 'New' | 'Open' | 'Export' | 'Print';

export type FileMenuItemConfig = BuiltInFileMenuItem | FileMenuItem;

/**
 * Build file menu items based on editor configuration
 */
export function buildFileMenuItems(config: EditorConfig): FileMenuItemConfig[] {
  const items: FileMenuItemConfig[] = [];
  const { documentType, canEdit, permissions, allowVersionCreationDisabled } = config;
  const canSave = canEdit || config.canComment === true;

  // New & Open: Only for regular/master when canEdit is true
  if (documentType !== 'control' && canEdit) {
    items.push('New');
    items.push('Open');
  }

  // Save: Based on permission and canEdit
  if (permissions.save) {
    items.push({
      id: 'save',
      text: 'Save',
      iconCss: 'e-icons e-save',
      disabled: !canSave,
    });
  }

  // Save As: Based on permission AND lifecycle template allowance
  // Hide the menu item entirely if version creation is disabled by lifecycle template
  if (permissions.saveAs) {
    items.push({
      id: 'save_as',
      text: 'Save As New Version',
      iconCss: 'e-icons e-save',
      disabled: config.canComment === true || allowVersionCreationDisabled === true,
    });
  }

  // Export: Based on permission
  if (permissions.export) {
    items.push('Export');
  }

  // Print: Based on permission
  if (permissions.print) {
    items.push('Print');
  }

  return items;
}

// =============================================================================
// RIBBON CONFIGURATION
// =============================================================================

export interface RibbonTabVisibility {
  developer: boolean;
  masterCopy: boolean;
}

/**
 * Determine ribbon tab visibility based on document type
 */
export function getRibbonTabVisibility(documentType: DocumentType): RibbonTabVisibility {
  return {
    developer: false, // Always hidden
    masterCopy: documentType !== 'control', // Hidden only for control copies
  };
}

// =============================================================================
// STATE TRANSITION VALIDATION
// =============================================================================

const VALID_TRANSITIONS: Record<EditorState, EditorState[]> = {
  IDLE: ['WAITING_FOR_CONFIG', 'CONFIGURING', 'ERROR'],
  WAITING_FOR_CONFIG: ['CONFIGURING', 'ERROR'],
  CONFIGURING: ['READY', 'APPLYING_PROTECTION', 'CONFIGURING', 'ERROR'], // Can go to APPLYING_PROTECTION (control copy path)
  READY: ['LOADING_DOCUMENT', 'ACTIVE', 'CONFIGURING', 'ERROR'],
  LOADING_DOCUMENT: ['APPLYING_PROTECTION', 'ACTIVE', 'CONFIGURING', 'ERROR'], // Can reinit mid-load
  APPLYING_PROTECTION: ['READY', 'CONFIGURING', 'ERROR'],            // Can reinit mid-protection
  ACTIVE: ['CONFIGURING', 'IDLE', 'ERROR'],                          // IDLE for full reset
  ERROR: ['IDLE', 'CONFIGURING'],                                     // Can reinit from error
};

/**
 * Check if a state transition is valid
 */
export function canTransition(from: EditorState, to: EditorState): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Get valid next states from current state
 */
export function getValidTransitions(from: EditorState): EditorState[] {
  return VALID_TRANSITIONS[from] ?? [];
}